import { toast } from 'sonner'

import { useModalContext } from '@/shared/hooks'
import { useFinishResource } from '@/shared/hooks/useFinishResource'
import {
  FilterChips,
  ListSection,
  PageHeader,
  PageHero,
  Screen
} from '@/shared/ui/Layout'
import { RewardBadge } from '@/shared/ui/RewardBadge'
import { Badge } from '@/shared/ui/Badge'
import { Resource } from '@/shared/ui/Resource'

import { ResourceCard } from './components/ResourceCard'
import { LibraryBottomSheet } from './BottomSheet'
import { useLibraryView } from './hooks/useLibraryView'

export const DigitalProductLibrary = () => {
  const { showModal } = useModalContext()
  const { finishResource } = useFinishResource()
  const {
    filter,
    visibleFilters,
    setFilter,
    grouped,
    stats,
    isEmpty,
    isFilteredEmpty
  } =
    useLibraryView()

  const handleFinishDocument = async (document: {
    id: number
    _finished: boolean
    point_reward?: number
    coin_reward?: number
  }) => {
    if (document._finished) return true

    const ok = await finishResource({
      type: 'Document',
      id: document.id,
      action: 'download'
    })

    if (
      ok &&
      ((document.point_reward ?? 0) > 0 || (document.coin_reward ?? 0) > 0)
    ) {
      toast.success('Recompensa recebida!', {
        description: 'Seu progresso foi atualizado.'
      })
    }

    return ok
  }

  const handleShowMaterial = (document: any) =>
    showModal({
      component: LibraryBottomSheet,
      ...document,
      locked: document._locked,
      onFinishResource: () => handleFinishDocument(document)
    })

  return (
    <Screen>
      <Screen.Context className="mb-4">
        <PageHeader title="Biblioteca" />

        {!isEmpty && (
          <PageHero art="/library/hero-icon.png" artFit="contain" variant="light">
            <PageHero.Title>Biblioteca</PageHero.Title>
            <PageHero.Subtitle>
              <span className="flex flex-wrap gap-1.5">
                <Badge variant="available" tone="light" size="sm" showIcon={false}>
                  {stats.total} materiais
                </Badge>
                {stats.totalPoints > 0 && (
                  <RewardBadge type="xp" value={stats.totalPoints} variant="inline" />
                )}
                {stats.totalCoins > 0 && (
                  <RewardBadge type="coins" value={stats.totalCoins} variant="inline" />
                )}
              </span>
            </PageHero.Subtitle>
            <PageHero.ProgressRow
              value={stats.percent}
              label={`${stats.completedCount}/${stats.total} concluídos`}
            />
          </PageHero>
        )}
      </Screen.Context>

      {!isEmpty && (
        <Screen.Sticky>
          <FilterChips
            options={visibleFilters}
            value={filter}
            onChange={id => setFilter(id as typeof filter)}
            aria-label="Filtrar materiais"
          />
        </Screen.Sticky>
      )}

      <Screen.Content className="pt-4">
        {isEmpty && <LibraryEmpty />}

        {isFilteredEmpty && (
          <LibraryEmpty filtered onClear={() => setFilter('all')} />
        )}

        {!isEmpty && !isFilteredEmpty && filter === 'all' && (
          <>
            {grouped.available.length > 0 && (
              <ListSection id="library-section-available" title="Ganhe recompensas">
                {grouped.available.map(doc => (
                  <Resource.Root
                    key={doc.id}
                    type="Document"
                    id={doc.id}
                    pointsReward={doc.point_reward}
                    coinsReward={doc.coin_reward}
                    unlock={{
                      journeyId: doc.unlock_journey_id,
                      missionId: doc.unlock_mission_id
                    }}
                  >
                    <ResourceCard
                      document={doc}
                      onOpen={() => handleShowMaterial(doc)}
                    />
                  </Resource.Root>
                ))}
              </ListSection>
            )}

            {grouped.completed.length > 0 && (
              <ListSection id="library-section-success" title="Concluídos">
                {grouped.completed.map(doc => (
                  <Resource.Root
                    key={doc.id}
                    type="Document"
                    id={doc.id}
                    pointsReward={doc.point_reward}
                    coinsReward={doc.coin_reward}
                    unlock={{
                      journeyId: doc.unlock_journey_id,
                      missionId: doc.unlock_mission_id
                    }}
                  >
                    <ResourceCard
                      document={doc}
                      onOpen={() => handleShowMaterial(doc)}
                    />
                  </Resource.Root>
                ))}
              </ListSection>
            )}

            {grouped.locked.length > 0 && (
              <ListSection id="library-section-locked" title="Em breve">
                {grouped.locked.map(doc => (
                  <Resource.Root
                    key={doc.id}
                    type="Document"
                    id={doc.id}
                    pointsReward={doc.point_reward}
                    coinsReward={doc.coin_reward}
                    unlock={{
                      journeyId: doc.unlock_journey_id,
                      missionId: doc.unlock_mission_id
                    }}
                  >
                    <ResourceCard
                      document={doc}
                      onOpen={() => handleShowMaterial(doc)}
                    />
                  </Resource.Root>
                ))}
              </ListSection>
            )}
          </>
        )}

        {!isEmpty && !isFilteredEmpty && filter !== 'all' && (
          <div className="flex flex-col gap-3">
            {[...grouped.available, ...grouped.completed, ...grouped.locked].map(doc => (
              <Resource.Root
                key={doc.id}
                type="Document"
                id={doc.id}
                pointsReward={doc.point_reward}
                coinsReward={doc.coin_reward}
                unlock={{
                  journeyId: doc.unlock_journey_id,
                  missionId: doc.unlock_mission_id
                }}
              >
                <ResourceCard
                  document={doc}
                  onOpen={() => handleShowMaterial(doc)}
                />
              </Resource.Root>
            ))}
          </div>
        )}

        {!isEmpty && (
          <p className="py-6 text-center text-xs font-semibold tracking-wide text-gray-500">
            Todos os materiais carregados
          </p>
        )}
      </Screen.Content>
    </Screen>
  )
}

// ─── Empty States ─────────────────────────────────────────────────────────────

function LibraryEmpty({
  filtered = false,
  onClear
}: {
  filtered?: boolean
  onClear?: () => void
}) {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-center">
      <span className="text-4xl">📚</span>
      <div>
        <p className="text-sm font-semibold text-gray-900">
          {filtered ? 'Nenhum material encontrado.' : 'Biblioteca vazia'}
        </p>
        <p className="mt-0.5 text-2xs text-[#7e8299]">
          {filtered
            ? 'Tente alterar os filtros.'
            : 'Nenhum material disponível no momento.'}
        </p>
      </div>
      {filtered && onClear && (
        <button
          type="button"
          onClick={onClear}
          className="rounded-full bg-[var(--color-brand-light)] px-4 py-2 text-2xs font-semibold text-[var(--color-brand)]"
        >
          Limpar filtros
        </button>
      )}
    </div>
  )
}
